import Pristine from 'pristinejs';

/**
 * Multi-step form module. Instantiate a new MultiStepForm(form) to set it all up.
 * The form should have buttons with the classes js-prev and js-next to navigate the form.
 * You can also add buttons with class "js-go-to-step" and data attribute "data-step"
 * to navigate to a specific step (e.g. data-step="0" to go to the beginning).
 * Add the class "js-submit" to your "next" button to get it to change to "Submit" on the final step.
 * Based on https://w3sniff.com/code?id=60&title=Multi-Step-Form-with-Progress-Bar-using-bootstrap-5
 */
export default class MultiStepForm {
  private form: HTMLFormElement;
  private submitFn?: () => void;
  /** Function to run before step show */
  private beforeStepFn?: () => void;
  /** Function to run after step show */
  private afterStepFn?: () => void;
  public currentTab: number;
  private prevBtns: NodeListOf<HTMLButtonElement>;
  private nextBtns: NodeListOf<HTMLButtonElement>;
  private goToStepBtns: NodeListOf<HTMLButtonElement>;
  private clearStepBtns: NodeListOf<HTMLButtonElement>;
  private tabs: HTMLCollectionOf<HTMLElement>;
  private stepIndicators: HTMLCollectionOf<HTMLElement>;
  private pristine: Pristine;
  constructor({ form, submitFn, beforeStepFn, afterStepFn, startTab }: {
    form: HTMLFormElement;
    submitFn?: () => void;
    beforeStepFn?: () => void;
    afterStepFn?: () => void;
    startTab?: number;
  }) {
    this.form = form;
    this.submitFn = submitFn;
    this.beforeStepFn = beforeStepFn;
    this.afterStepFn = afterStepFn;
    this.currentTab = startTab ?? 0;
    this.prevBtns = this.form.querySelectorAll<HTMLButtonElement>('.js-prev');
    this.nextBtns = this.form.querySelectorAll<HTMLButtonElement>('.js-next');
    this.goToStepBtns = this.form.querySelectorAll<HTMLButtonElement>('.js-go-to-step');
    this.clearStepBtns = this.form.querySelectorAll<HTMLButtonElement>('.js-clear-step');
    this.tabs = this.form.getElementsByClassName('step') as HTMLCollectionOf<HTMLElement>;
    this.stepIndicators = this.form.getElementsByClassName('step-indicator') as HTMLCollectionOf<HTMLElement>;

    this.init();
  }

  /** Display the specified tab of the form */
  private showTab(n: number) {
    // Run Step function if provided
    // We do this before changing tabs so that if the next tab is updating, it's changed before showing
    if (this.beforeStepFn) {
      this.beforeStepFn();
    }

    // Display the specified tab
    for (let i = 0; i < this.tabs.length; i++) {
      const isActive = (i === n);
      this.tabs[i].style.display = isActive ? 'block' : 'none';
    }

    // ... and fix the Previous/Next buttons:
    if (n === 0) {
      this.prevBtns.forEach((btn) => {
        // btn.style.visibility = 'hidden';
        btn.setAttribute('disabled', 'disabled');
        // btn.setAttribute('aria-hidden', 'true');
      });
    } else {
      this.prevBtns.forEach((btn) => {
        btn.style.visibility = 'visible';
        btn.removeAttribute('disabled');
        btn.removeAttribute('aria-hidden');
      });
    }
    if (n === (this.tabs.length - 1)) {
      this.nextBtns.forEach((btn) => {
        // Only change text on 'js-submit' buttons since the "skip this step" buttons should still say "Next"
        if (btn.classList.contains('js-submit')) {
          btn.innerHTML = 'Finish';
        }
      });
    } else {
      this.nextBtns.forEach((btn) => {
        if (btn.classList.contains('js-submit')) {
          btn.innerHTML = 'Next';
        }
      });
    }

    // // For RC we're hiding the next button on the tier step, since it has tier buttons that trigger next()
    // if (this.tabs[n].dataset.hideNextBtn) {
    //   this.nextBtn!.style.display = 'none';
    // } else {
    //   this.nextBtn!.style.display = 'inline';
    // }

    // ... and run a function that will display the correct step indicator:
    this.setStepIndicators(n);

    // Run after step function (e.g. if something needs to be visible for the code to work)
    if (this.afterStepFn) {
      this.afterStepFn();
    }
  }

  /** Validate current tab and go to next one or submit form if at end */
  public next() {
    // Exit the function if any field in the current tab is invalid:
    if (!this.validateForm()) {
      return;
    }
    // Increase the current tab
    this.currentTab += 1;
    // if you have reached the end of the form...
    if (this.currentTab >= this.tabs.length) {
      // ...fire submit event (form.submit() doesn't trigger it)
      this.form.dispatchEvent(new Event('submit', { cancelable: true }));
      return;
    }
    // Otherwise, display the correct tab:
    this.showTab(this.currentTab);
  }

  /** Go to previous tab */
  public prev() {
    // Decrease the current tab
    this.currentTab -= 1;
    // Min zero
    if (this.currentTab < 0) {
      this.currentTab = 0;
    }
    // Display the correct tab:
    this.showTab(this.currentTab);
  }

  /** Set the "active" and "finished" classes on the steps, as needed */
  private setStepIndicators(n: number) {
    for (let i = 0; i < this.stepIndicators.length; i++) {
      const isActive = (i === n);
      const isFinished = (i < n);
      this.stepIndicators[i].classList.toggle('active', isActive);
      this.stepIndicators[i].classList.toggle('finish', isFinished);
    }
  }

  /** Clear the form input values of a specific step */
  private clearStepValues(n: number) {
    const inputs = this.tabs[n].querySelectorAll('input, select, textarea');
    inputs.forEach((input) => {
      if (input instanceof HTMLInputElement) {
        // If it's a dual-range slider, "reset" to the min + max values
        if (input.classList.contains('dual-handle-range__input--left')) {
          input.value = input.getAttribute('min') || '';
          // Fire input event to update the slider position
          input.dispatchEvent(new Event('input'));
        } else if (input.classList.contains('dual-handle-range__input--right')) {
          input.value = input.getAttribute('max') || '';
          input.dispatchEvent(new Event('input'));
        } else if (input.type === 'checkbox') {
          // If it's a checkbox, uncheck it
          input.checked = false;
        } else if (input.type === 'radio') {
          // If it's a radio, uncheck all in the group
          const radioGroup = document.querySelectorAll<HTMLInputElement>(`input[name="${input.name}"]`);
          radioGroup.forEach((radio) => {
            radio.checked = false;
          });
        } else {
          // Otherwise reset to empty string
          input.value = '';
        }
      } else if (input instanceof HTMLSelectElement) {
        input.selectedIndex = 0;
      }
    });
  }

  private init() {
    // Display the current tab
    this.showTab(this.currentTab);

    // Hook up prev/next buttons
    this.prevBtns.forEach((btn) => {
      btn.addEventListener('click', () => {
        this.prev();
      });
    });
    this.nextBtns.forEach((btn) => {
      btn.addEventListener('click', () => {
        this.next();
      });
    });
    this.goToStepBtns.forEach((btn) => {
      btn.addEventListener('click', (event) => {
        const target = event.currentTarget as HTMLButtonElement;
        const step = parseInt(target.getAttribute('data-step') || '0', 10);
        this.currentTab = step;
        this.showTab(step);
      });
    });
    this.clearStepBtns.forEach((btn) => {
      btn.addEventListener('click', () => {
        this.clearStepValues(this.currentTab);
      });
    });

    // Hook up form submit, if provided
    if (this.submitFn) {
      // console.debug('!! MultiStepForm adding submit listener');
      this.form.addEventListener('submit', (e) => {
        // console.debug('!! MultiStepForm submit');
        e.preventDefault();
        this.submitFn!();
      });
    }
  }

  /** Validate the form fields for the current tab */
  private validateForm(): boolean {
    const currTabInputs = this.tabs[this.currentTab].querySelectorAll('input, select, textarea');

    // We instantiate a new Pristine instance instead of just making it a class property
    // because if we use Vue to change required attributes, Pristine doesn't pick up on it.
    if (this.pristine) {
      this.pristine.destroy();
    }
    this.pristine = new Pristine(this.form);

    // If the valid status is true, mark the step as finished and valid:
    const isValid = this.pristine.validate(currTabInputs);
    // console.debug('validateForm', { currTabInputs, isValid });
    return isValid;
  }
};
